/**
 * Onboarding asset-reservering + bruikleen — types + Zod-schemas voor PR C5.
 *
 * Bron: spec C §6 (asset-toewijzing vanuit onboarding) + §16.3 (edge cases:
 * dubbele voertuig-reservering, retour-tijdens-onboarding).
 *
 * Vier endpoints onder `/api/v1/onboarding/:id/assets*`:
 *   GET    /:id/assets                                  — overzicht
 *   POST   /:id/assets                                  — reservering aanmaken
 *   PATCH  /:id/assets/:reserveringId                   — status-wijziging
 *   DELETE /:id/assets/:reserveringId                   — soft-cancel (→ NIET_BESCHIKBAAR)
 *
 * Status-flow (spec §6.1):
 *   GERESERVEERD → BESTELD          (HR plaatst bestelling)
 *   GERESERVEERD/BESTELD → TOEGEWEZEN (definitief gekoppeld; maakt Loan + bruikleen-PDF + VoertuigToewijzing/UitrustingItem)
 *   TOEGEWEZEN → INGENOMEN          (alleen na onboarding ACTIEF — spec §16.3)
 *   * → NIET_BESCHIKBAAR             (handmatig of "soft delete" via DELETE)
 */

import { z } from 'zod'
import { idParam } from './id-validators'
import { MUTATIE_MODUS, type MutatieModus } from './leverwijze'

// ─── Enums (matching Prisma `OnboardingAssetType` / `OnboardingAssetStatus`) ─

export const ONBOARDING_ASSET_TYPE = [
  'VOERTUIG',
  'TELEFOON',
  'GEREEDSCHAP',
  'KLEDING',
  'LAPTOP',
  'TOEGANGSPAS',
  'OVERIG',
] as const
export type OnboardingAssetType = (typeof ONBOARDING_ASSET_TYPE)[number]

export const ONBOARDING_ASSET_STATUS = [
  'GERESERVEERD',
  'BESTELD',
  'TOEGEWEZEN',
  'NIET_BESCHIKBAAR',
] as const
export type OnboardingAssetStatus = (typeof ONBOARDING_ASSET_STATUS)[number]

// Spec §16.3 — "ingenomen" is geen Prisma-status maar een UI-marker voor de
// Loan-status. Documenteer als optionele type-uitbreiding zodat de UI weet
// dat een retour-actie pas na ACTIEF mag.
export const ONBOARDING_ASSET_TYPE_LABELS: Record<OnboardingAssetType, string> = {
  VOERTUIG:    'Voertuig',
  TELEFOON:    'Telefoon',
  GEREEDSCHAP: 'Gereedschap',
  KLEDING:     'Kleding',
  LAPTOP:      'Laptop',
  TOEGANGSPAS: 'Toegangspas',
  OVERIG:      'Overig',
}

export const ONBOARDING_ASSET_STATUS_LABELS: Record<OnboardingAssetStatus, string> = {
  GERESERVEERD:      'Gereserveerd',
  BESTELD:           'Besteld',
  TOEGEWEZEN:        'Toegewezen',
  NIET_BESCHIKBAAR:  'Niet beschikbaar',
}

// ─── DTOs ───────────────────────────────────────────────────────────────────

export interface OnboardingAssetReserveringDTO {
  id:                   string
  onboardingId:         string
  type:                 OnboardingAssetType
  status:               OnboardingAssetStatus
  voertuigId:           string | null
  uitrustingItemId:     string | null
  voorraadUniekItemId:  string | null
  /**
   * Korte labels voor UI-rendering (zonder extra join client-side).
   *  - VOERTUIG → kenteken / merkModel
   *  - KLEDING/TELEFOON/etc → maat + opmerkingen
   *  - VOORRAAD_UNIEK → artikelNaam + serienummer
   */
  label:                string | null
  /**
   * Per-onboarding bijtelling-keuze voor een gekoppeld VOERTUIG (privégebruik).
   * Alleen gevuld voor type=VOERTUIG; voorgevuld uit `Voertuig.bijtelling` bij
   * koppelen (door HR overschrijfbaar). Bepaalt de auto-overeenkomst-variant
   * (MET_/ZONDER_BIJTELLING). `null` voor andere typen of als niet gezet.
   */
  bijtelling:           boolean | null
  toegewezenDoor:       string | null
  opmerkingen:          string | null
  /**
   * batch-45 onboarding (FO-381) — expliciete mutatie-modus van deze regel
   * (VOORRAAD / BESTELLING / GEEN_MUTATIE). `null` = legacy rij van vóór
   * FO-381 (impliciet gedrag: uniek-ref -> voorraad, geen ref -> registratie).
   */
  mutatieModus:         MutatieModus | null
  /** Bij BESTELLING: de gekoppelde bestel-aanvraag (uitgifte volgt bij ontvangst). */
  bestelAanvraagId:     string | null
  createdAt:            string
  updatedAt:            string
}

// ─── Zod-schemas ─────────────────────────────────────────────────────────────

/**
 * POST `/onboarding/:id/assets` — maak een reservering aan.
 *
 * Polymorf: precies één van (voertuigId, uitrustingItemId, voorraadUniekItemId)
 * moet gevuld zijn voor de typen die een referentie hebben (VOERTUIG →
 * voertuigId, etc.). Type OVERIG mag zonder referentie.
 *
 * `superRefine` valideert de polymorfe constraint zodat Zod de fout
 * meteen returnt als VALIDATION_ERROR.
 */
export const assetReserveringMakenSchema = z
  .object({
    type:                z.enum(ONBOARDING_ASSET_TYPE),
    voertuigId:          idParam('voertuigId').optional(),
    uitrustingItemId:    idParam('uitrustingItemId').optional(),
    voorraadUniekItemId: idParam('voorraadUniekItemId').optional(),
    status:              z.enum(ONBOARDING_ASSET_STATUS).default('GERESERVEERD'),
    opmerkingen:         z.string().trim().max(2000).optional(),
    // Per-onboarding bijtelling-keuze voor een gekoppeld VOERTUIG (privégebruik).
    // Alleen relevant voor type=VOERTUIG; voorgevuld uit Voertuig.bijtelling
    // wanneer niet meegegeven (door HR overschrijfbaar). Bepaalt de auto-
    // overeenkomst-variant (MET_/ZONDER_BIJTELLING).
    bijtelling:          z.boolean().optional(),
    // batch-45 onboarding (FO-381 §3.3) — expliciete mutatie-modus per regel.
    // Weggelaten = legacy gedrag (uniek-ref -> voorraad, geen ref -> registratie
    // zonder markering). BESTELLING vereist een voorraadArtikelId (het artikel
    // dat besteld moet worden); GEEN_MUTATIE registreert alleen het bezit.
    mutatieModus:        z.enum(MUTATIE_MODUS).optional(),
    voorraadArtikelId:   idParam('voorraadArtikelId').optional(),
  })
  .superRefine((data, ctx) => {
    const refs = [data.voertuigId, data.uitrustingItemId, data.voorraadUniekItemId].filter(Boolean).length
    // VOERTUIG-type vereist voertuigId; KLEDING/TELEFOON/etc kan via uitrustingItemId of
    // voorraadUniekItemId. OVERIG mag zonder referentie.
    if (data.type === 'VOERTUIG' && !data.voertuigId) {
      ctx.addIssue({
        code:    'custom',
        path:    ['voertuigId'],
        message: 'voertuigId is verplicht voor type VOERTUIG',
      })
    }
    if (refs > 1) {
      ctx.addIssue({
        code:    'custom',
        message: 'Slechts één van voertuigId / uitrustingItemId / voorraadUniekItemId mag gevuld zijn',
      })
    }
    // FO-381 — modus-specifieke eisen. Een VOERTUIG kent geen mutatie-modus
    // (toewijzing loopt via VoertuigToewijzing, geen voorraad).
    if (data.mutatieModus && data.type === 'VOERTUIG') {
      ctx.addIssue({
        code:    'custom',
        path:    ['mutatieModus'],
        message: 'Een voertuig-toewijzing kent geen mutatie-modus',
      })
    }
    if (data.mutatieModus === 'VOORRAAD' && !data.voorraadUniekItemId) {
      ctx.addIssue({
        code:    'custom',
        path:    ['voorraadUniekItemId'],
        message: 'Kies een voorraad-item om uit voorraad toe te wijzen',
      })
    }
    if (data.mutatieModus === 'BESTELLING' && !data.voorraadArtikelId) {
      ctx.addIssue({
        code:    'custom',
        path:    ['voorraadArtikelId'],
        message: 'Kies het artikel dat besteld moet worden',
      })
    }
    if (data.mutatieModus === 'GEEN_MUTATIE' && refs > 0) {
      ctx.addIssue({
        code:    'custom',
        path:    ['mutatieModus'],
        message: 'Geen mutaties registreert alleen het bezit; koppel geen voorraad- of uitrustings-item',
      })
    }
    if (data.voorraadArtikelId && data.mutatieModus !== 'BESTELLING') {
      ctx.addIssue({
        code:    'custom',
        path:    ['voorraadArtikelId'],
        message: 'voorraadArtikelId hoort alleen bij de modus "Uit bestelling"',
      })
    }
  })
export type AssetReserveringMakenInput = z.infer<typeof assetReserveringMakenSchema>

/**
 * PATCH `/onboarding/:id/assets/:reserveringId` — status-wijziging +
 * optionele `toegewezenDoor` userId (gevuld bij TOEGEWEZEN-transitie).
 *
 * Geldige overgangen worden in de route geverifieerd (zie
 * `isAssetStatusOvergangToegestaan` in onboarding-assets.ts route).
 */
export const assetReserveringStatusSchema = z.object({
  status:         z.enum(ONBOARDING_ASSET_STATUS),
  toegewezenDoor: idParam('toegewezenDoor').optional(),
  opmerkingen:    z.string().trim().max(2000).optional(),
  // Optionele bijtelling-update voor een gekoppeld VOERTUIG (privégebruik).
  // Alleen toegepast voor type=VOERTUIG; bepaalt de auto-overeenkomst-variant.
  bijtelling:     z.boolean().optional(),
})
export type AssetReserveringStatusInput = z.infer<typeof assetReserveringStatusSchema>

// ─── Asset-checker DTOs (spec §6.2) ──────────────────────────────────────────

export interface AssetCheckerVoertuigOptie {
  id:                       string
  kenteken:                 string
  merkModel:                string
  geschiktVoorBijtelling:   boolean
}

export interface AssetCheckerTaakSuggestie {
  /** Korte titel (gebruikt als Taak.titel). */
  titel:           string
  /** Vrije omschrijving (gebruikt als Taak.inhoud). */
  beschrijving:    string
  /** Indicatie wie de taak zou moeten oppakken — vrij tekstveld voor UI. */
  verantwoordelijke?: string
}

export interface AssetCheckerVoertuigen {
  beschikbaar:              AssetCheckerVoertuigOptie[]
  /** True als de medewerker bijtelling toegestaan heeft (afgeleid uit voorkeur). */
  geschiktVoorBijtelling:   boolean
}

export interface AssetCheckerKleding {
  shirtBeschikbaar:    boolean
  broekBeschikbaar:    boolean
  schoenenBeschikbaar: boolean
  /** Vrije labels: 'shirt M', 'broek 52', 'schoen 43'. */
  ontbrekend:          string[]
}

export interface AssetCheckerTelefoons {
  opVoorraad:  boolean
  bestelNodig: boolean
}

export interface AssetCheckerGereedschap {
  completeSet: boolean
  ontbrekend:  string[]
}

export interface AssetCheckResultDTO {
  voertuigen:   AssetCheckerVoertuigen
  kleding:      AssetCheckerKleding
  telefoons:    AssetCheckerTelefoons
  gereedschap:  AssetCheckerGereedschap
  /**
   * Spec §6.2 — auto-suggestie taken voor ontbrekende assets. PR C5 retourneert
   * deze in de DTO en logt ze; daadwerkelijke Taak-records worden pas in een
   * vervolg-PR aangemaakt (taken-infra integratie buiten C5-scope; zie
   * implementation-note in service-bestand).
   */
  taken:        AssetCheckerTaakSuggestie[]
}
